import { Alert, Button, Form, InputNumber, Modal, Space, Table } from "antd";
import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../contexts/ToastContext";
import { useUser } from "../../../../contexts/UserContext/UserContext";
import apiClient from "../../../../helpers/apiClient";
import SingleRefundModal from "./SingleRefundModal";
import { isSolaGateway, newIdempotencyKey } from "../../../../lib/sola/solaPaymentApi";

// ── V2 Stripe Refund Form ────────────────────────────────────────────────────

const V2RefundForm = ({ resDetails, maxRefundable, multipleIntents, onSuccess, onCancel }) => {
    const { t } = useTranslation(["reservations"]);
    const { state } = useUser();
    const toast = useToast();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const handleFinish = async ({ amount }) => {
        try {
            setLoading(true);
            const res = await apiClient.post(
                "/api/payment-v2/cancel-payment-intent",
                { resID: resDetails.id, amount },
                { headers: { "company-id": state?.user?.companyId } },
            );

            if (res?.data?.success) {
                toast.success({
                    message: t("payment.refundSuccess"),
                    description: t("payment.refundInitiated"),
                });
                onSuccess();
            }
        } catch (error) {
            toast.error({
                message: t("payment.refundFailed"),
                description: error?.response?.data?.message ?? t("payment.refundFailed"),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            initialValues={{ amount: maxRefundable > 0 ? maxRefundable : undefined }}
            onFinish={handleFinish}>
            <p className="text-slate-500 text-sm mb-4">{t("payment.stripeRefundNote")}</p>

            {multipleIntents && (
                <Alert type="warning" showIcon className="mb-4" message={t("payment.multiplePaymentsWarning")} />
            )}

            {maxRefundable <= 0 ? (
                <Alert type="info" showIcon message={t("payment.noRefundablePayments")} />
            ) : (
                <>
                    <Form.Item
                        name="amount"
                        label={`${t("payment.refundAmount")} (${t("payment.maxRefundable")}: $${maxRefundable.toFixed(2)})`}
                        rules={[
                            { required: true, message: t("payment.amountRequired") },
                            {
                                validator: (_, value) => {
                                    if (!value || value <= 0) return Promise.reject(new Error(t("payment.amountMustBePositive")));
                                    if (value > maxRefundable) return Promise.reject(new Error(t("payment.amountExceedsMax")));
                                    return Promise.resolve();
                                },
                            },
                        ]}>
                        <InputNumber prefix="$" min={0.01} max={maxRefundable} step={0.01} precision={2} className="w-full" />
                    </Form.Item>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button onClick={onCancel} disabled={loading}>
                            {t("resDetails.cancel")}
                        </Button>
                        <Button htmlType="submit" type="primary" danger loading={loading}>
                            {t("payment.refund")}
                        </Button>
                    </div>
                </>
            )}
        </Form>
    );
};

V2RefundForm.propTypes = {
    resDetails: PropTypes.object.isRequired,
    maxRefundable: PropTypes.number.isRequired,
    multipleIntents: PropTypes.bool.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

// ── Sola Refund Form ─────────────────────────────────────────────────────────

/**
 * Refunds Sola card payments. The backend refunds the newest payment first, returns SoftCar's
 * share with it when the sale was split, and records a refund line on the invoice.
 */
const SolaRefundForm = ({ resDetails, maxRefundable, onSuccess, onCancel }) => {
    const { t } = useTranslation(["reservations", "reservationPayment"]);
    const { state } = useUser();
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    // Kept across a retry that never got an answer, so it replays instead of refunding twice;
    // replaced once the server has answered.
    const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

    const handleFinish = async ({ amount }) => {
        try {
            setLoading(true);
            const res = await apiClient.post(
                "/api/sola-payment/refund",
                { reservationId: resDetails.id, amount, idempotencyKey },
                { headers: { "company-id": state?.user?.companyId } },
            );

            if (res?.data?.success) {
                toast.success({
                    message: t("payment.refundSuccess"),
                    description: t("payment.refundInitiated"),
                });
                onSuccess();
            }
        } catch (error) {
            if (error?.response) setIdempotencyKey(newIdempotencyKey());
            toast.error({
                message: t("payment.refundFailed"),
                description: error?.response?.data?.message ?? t("payment.refundFailed"),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form
            layout="vertical"
            initialValues={{ amount: maxRefundable > 0 ? maxRefundable : undefined }}
            onFinish={handleFinish}>
            <p className="text-slate-500 text-sm mb-4">{t("reservationPayment:solaRefund.note")}</p>

            {maxRefundable <= 0 ? (
                <Alert type="info" showIcon message={t("payment.noRefundablePayments")} />
            ) : (
                <>
                    <Form.Item
                        name="amount"
                        label={`${t("payment.refundAmount")} (${t("payment.maxRefundable")}: $${maxRefundable.toFixed(2)})`}
                        rules={[
                            { required: true, message: t("payment.amountRequired") },
                            {
                                validator: (_, value) => {
                                    if (!value || value <= 0) return Promise.reject(new Error(t("payment.amountMustBePositive")));
                                    if (value > maxRefundable) return Promise.reject(new Error(t("payment.amountExceedsMax")));
                                    return Promise.resolve();
                                },
                            },
                        ]}>
                        <InputNumber prefix="$" min={0.01} max={maxRefundable} step={0.01} precision={2} className="w-full" />
                    </Form.Item>

                    <div className="flex justify-end gap-2 pt-2">
                        <Button onClick={onCancel} disabled={loading}>
                            {t("resDetails.cancel")}
                        </Button>
                        <Button htmlType="submit" type="primary" danger loading={loading}>
                            {t("payment.refund")}
                        </Button>
                    </div>
                </>
            )}
        </Form>
    );
};

SolaRefundForm.propTypes = {
    resDetails: PropTypes.object.isRequired,
    maxRefundable: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

// ── RefundModal2 ─────────────────────────────────────────────────────────────

const RefundModal2 = ({ open, onClose, resDetails, refetch }) => {
    const { t } = useTranslation(["reservations", "reservationPayment"]);
    const [refundItem, setRefundItem] = useState(null);

    const { myCompanyData } = useUser();
    const { hasV2Stripe, maxRefundable, multipleIntents, hasSola, solaMaxRefundable } = useMemo(() => {
        const raw = resDetails?.invoice?.paymentEvents ?? resDetails?.invoice?.paymentEvent;
        const events = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];

        const checkouts = events.filter(
            (e) =>
                e.paymentPlatform === "stripe" &&
                e.eventType === "checkout" &&
                e.status === "success" &&
                e.paymentIntentId,
        );

        const captured = checkouts.reduce((s, e) => s + Number(e.amount ?? 0), 0);
        const refundedSoFar = events
            .filter((e) => e.eventType === "refund" && e.status === "success")
            .reduce((s, e) => s + Number(e.amount ?? 0), 0);

        const isSola = (e, type) => e.paymentPlatform === "sola" && e.eventType === type && e.status === "success";
        const solaCollected = events.filter((e) => isSola(e, "checkout") && e.paymentIntentId);
        const solaRefunded = events.filter((e) => isSola(e, "refund")).reduce((s, e) => s + Number(e.amount ?? 0), 0);
        const solaTotal = solaCollected.reduce((s, e) => s + Number(e.amount ?? 0), 0);

        return {
            hasV2Stripe: checkouts.length > 0,
            maxRefundable: Math.max(0, captured - refundedSoFar),
            multipleIntents: checkouts.length > 1,
            hasSola: solaCollected.length > 0,
            solaMaxRefundable: Math.round(Math.max(0, solaTotal - solaRefunded) * 100) / 100,
        };
    }, [resDetails]);

    const closeAll = () => {
        setRefundItem(null);
        onClose();
    };

    if (!resDetails) return null;

    // ── Sola path ───────────────────────────────────────────────────────────
    // Chosen when the reservation holds Sola card payments. A reservation paid through both
    // gateways follows the company's current gateway.
    if (hasSola && (!hasV2Stripe || isSolaGateway(myCompanyData))) {
        return (
            <Modal
                open={open}
                onCancel={closeAll}
                maskClosable={false}
                closable
                title={t("reservationPayment:solaRefund.title")}
                footer={null}>
                <SolaRefundForm
                    resDetails={resDetails}
                    maxRefundable={solaMaxRefundable}
                    onSuccess={() => {
                        refetch();
                        onClose();
                    }}
                    onCancel={closeAll}
                />
            </Modal>
        );
    }

    // ── V2 Stripe path ──────────────────────────────────────────────────────
    if (hasV2Stripe) {
        return (
            <Modal open={open} onCancel={closeAll} maskClosable={false} closable title={t("payment.stripeRefund")} footer={null}>
                <V2RefundForm
                    resDetails={resDetails}
                    maxRefundable={maxRefundable}
                    multipleIntents={multipleIntents}
                    onSuccess={() => { refetch(); onClose(); }}
                    onCancel={closeAll}
                />
            </Modal>
        );
    }

    // ── V1 legacy path ──────────────────────────────────────────────────────
    const columns = [
        {
            title: t("payment.description"),
            dataIndex: "description",
            key: "description",
            // biome-ignore lint/a11y/useValidAnchor: <explanation>
            render: (text) => <a>{text}</a>,
        },
        {
            title: t("payment.amount"),
            dataIndex: "amount",
            key: "amount",
        },
        {
            title: t("action"),
            key: "action",
            render: (_, record) => (
                <Space size="middle">
                    <Button
                        onClick={() => setRefundItem(record)}
                        color={record.refunded ? "default" : "danger"}
                        variant="solid"
                        disabled={record.refunded}>
                        {record.refunded ? t("payment.refunded") : t("payment.refund")}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Modal open={open} onCancel={closeAll} maskClosable={false} closable title={t("payment.listOfPayments")} footer={false}>
            <Table
                className="sc-table-frame"
                columns={columns}
                dataSource={[
                    {
                        ...resDetails,
                        description: t("payment.initialPayment"),
                        amount: resDetails.initialPayment || resDetails.amount,
                        paymentIntentId: resDetails.paymentIntent,
                    },
                    ...(resDetails.extraCharge || []).map((item) => ({
                        ...item,
                        description: item.description,
                        amount: item.total,
                        paymentIntentId: item.paymentIntentId,
                    })),
                ]}
            />
            <div className="relative">
                <SingleRefundModal
                    closeSingleRefundModal={() => setRefundItem(null)}
                    closeParentModal={closeAll}
                    refundItem={refundItem}
                    resDetails={resDetails}
                    refetch={refetch}
                />
            </div>
        </Modal>
    );
};

RefundModal2.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    resDetails: PropTypes.object,
    refetch: PropTypes.func,
};

export default RefundModal2;
